import { type FC, useState } from "react";

import { CancelSaveMenu } from "components/menus/CancelSaveMenu";
import { InteractionMode } from "components/server-types";
import { t } from "i18n/i18n";
import { RadioButton } from "mds/components/RadioButton";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ChevronDownIcon, GearSettingsIcon } from "mds/icons";
import { storeApi } from "store/index";
import { NestedPageGroupType, pageSelectors } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { GROUP_INTERACTION_MODES, GroupInteractionModeType } from "utils/page-utils";

interface InteractionModeConfigProps {
  pageId: string;
  pageGroups: NestedPageGroupType[];
  isDisabled?: boolean;
  isReadOnly?: boolean;
  title: string;
}

type GroupInteractionMode = InteractionMode.ASSIGN_STUDENTS | InteractionMode.SELF_ASSIGN;

export const InteractionModeConfig: FC<InteractionModeConfigProps> = ({
  pageId,
  pageGroups,
  isDisabled,
  isReadOnly,
  title,
}) => {
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["interaction_mode"]);
  const interactionMode = page?.interaction_mode as GroupInteractionMode | null;
  const [currentInteractionMode, setCurrentInteractionMode] = useState<GroupInteractionMode | null>(
    interactionMode,
  );

  if (interactionMode === null || currentInteractionMode === null) {
    return null;
  }

  const onSave = () => {
    storeApi.pages.partial_update({ id: pageId, interaction_mode: currentInteractionMode });

    // When switching group mode to "ASSIGN_STUDENTS", mark all staged students
    // (who may be in this state because of student self-select) as ready.
    if (currentInteractionMode === InteractionMode.ASSIGN_STUDENTS) {
      pageGroups.forEach((pageGroup) => {
        pageGroup.page_group_users.forEach((pageGroupUser) => {
          storeApi.page_group_users // TODO: Bulk update
            .partial_update({
              id: pageGroupUser.id,
              is_ready: true,
            });
        });
      });
    }
  };

  return (
    <Tooltip content={isDisabled || isReadOnly ? title : ""}>
      <CancelSaveMenu
        button={
          <SquareButton
            aria-label={!isDisabled && !isReadOnly ? title : undefined}
            disabled={isDisabled || isReadOnly}
            kind="tertiary"
            size="s"
            title={!isDisabled && !isReadOnly ? title : ""}
          >
            <GearSettingsIcon />
            {t(`group_configuration_card.group_type.${interactionMode}`)}
            {!isReadOnly && <ChevronDownIcon />}
          </SquareButton>
        }
        saveEventName="Group selection method updated"
        saveEventProps={{ fieldValue: currentInteractionMode }}
        title={t("group_configuration_card.config.interaction_mode")}
        onCancel={() => setCurrentInteractionMode(interactionMode)}
        onSave={onSave}
      >
        <div className="flex flex-col gap-1">
          {GROUP_INTERACTION_MODES.map((choice) => {
            return (
              <RadioButton
                checked={currentInteractionMode === choice}
                id={choice}
                key={choice}
                name="pageGroupType"
                value={choice}
                onChange={(event) => {
                  setCurrentInteractionMode(event.target.value as GroupInteractionMode);
                }}
              >
                {t(`group_configuration_card.choice.${choice as GroupInteractionModeType}`)}
              </RadioButton>
            );
          })}
        </div>
      </CancelSaveMenu>
    </Tooltip>
  );
};
